import React, { Suspense } from 'react';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import Layout from 'components/Layout';
import SEO from 'components/SEO';

// Lazily-loaded pages
const LandingPage = React.lazy(() => import('pages/LandingPage'));
const CatererDiscovery = React.lazy(() => import('pages/caterer-discovery'));
const CatererProfile = React.lazy(() => import('pages/caterer-profile'));
const CustomRequest = React.lazy(() => import('pages/custom-request'));
const Checkout = React.lazy(() => import('pages/checkout'));
const OrderManagement = React.lazy(() => import('pages/order-management'));
const CustomerDashboard = React.lazy(() => import('pages/customer-dashboard'));
const MessagesPage = React.lazy(() => import('pages/messages'));
const PaymentHistory = React.lazy(() => import('pages/payment-history'));
const PayOrderPage = React.lazy(() => import('pages/pay-order'));
const PaymentCallback = React.lazy(() => import('pages/payment-callback'));
const BidComparison = React.lazy(() => import('pages/bid-comparison'));
const MobilePay = React.lazy(() => import('pages/mobile-pay'));
const ResetPassword = React.lazy(() => import('pages/reset-password'));
const ContactUs = React.lazy(() => import('pages/contact-us'));
const CatererVerification = React.lazy(() => import('pages/caterer-verification'));
const TrustScore = React.lazy(() => import('pages/trust-score'));
const EscrowFlow = React.lazy(() => import('pages/escrow-flow'));
const TermsOfService = React.lazy(() => import('pages/terms-of-service'));
const PrivacyPolicy = React.lazy(() => import('pages/privacy-policy'));
const RefundPolicy = React.lazy(() => import('pages/refund-policy'));
const Compliance = React.lazy(() => import('pages/compliance'));
const AcceptableUsePolicy = React.lazy(() => import('pages/acceptable-use-policy'));
const ShippingPolicy = React.lazy(() => import('pages/shipping-policy'));
const ReturnsPolicy = React.lazy(() => import('pages/returns-policy'));
const SubmitIdea = React.lazy(() => import('pages/submit-idea'));
const ReportProblem = React.lazy(() => import('pages/report-problem'));
const DeleteAccount = React.lazy(() => import('pages/delete-account'));
const Referral = React.lazy(() => import('pages/referral'));
const ClaimBusiness = React.lazy(() => import('pages/claim-business'));
const NotFound = React.lazy(() => import('pages/NotFound'));

const OnboardingLockScreen = () => {
  return (
    <Layout>
      <SEO
        title="Discover trusted event caterers in South Africa"
        description="Explore verified Mrs Cater partners for weddings, funerals, corporate events and private functions across South Africa."
        canonical="/"
        noindex
      />
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-background">
        <div className="max-w-xl w-full bg-card border border-border rounded-2xl shadow-sm px-6 py-8 sm:px-10 sm:py-10 text-center space-y-6">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700/80">
              Mrs Cater™
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
              We&apos;re busy setting the table
            </h1>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            Discover trusted event caterers across South Africa. Browse verified partners, compare menus,
            and request quotes for weddings, funerals, corporate events and private functions.
          </p>
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-left text-xs sm:text-sm text-amber-900">
            <p className="font-semibold mb-1">Are you a caterer?</p>
            <p>
              Access your caterer portal to manage your profile, menu and incoming requests.
            </p>
          </div>
          <div className="space-y-3">
            <a
              href="https://caterer.mrscater.com/login"
              className="inline-flex items-center justify-center px-5 py-3 rounded-full bg-amber-900 text-amber-50 text-sm font-semibold shadow-sm hover:bg-amber-800 transition-colors"
            >
              Access Caterer Portal
            </a>
            <p className="text-[11px] text-muted-foreground">
              Already a caterer on Mrs Cater? Visit your{' '}
              <a
                href="https://caterer.mrscater.com"
                className="underline underline-offset-2 hover:text-foreground"
              >
                caterer dashboard
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const Routes = () => {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
        <RouterRoutes>
          <Route path="/" element={<Layout><LandingPage /></Layout>} />
          <Route path="/caterer-discovery" element={<Layout><CatererDiscovery /></Layout>} />
          <Route path="/caterer-profile/:catererId" element={<Layout><CatererProfile /></Layout>} />
          <Route path="/custom-request" element={<Layout><CustomRequest /></Layout>} />
          <Route path="/bid-comparison" element={<Layout><BidComparison /></Layout>} />
          <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
          <Route path="/order-management" element={<Layout><OrderManagement /></Layout>} />
          <Route path="/customer-dashboard" element={<Layout><CustomerDashboard /></Layout>} />
          <Route path="/messages" element={<Layout><MessagesPage /></Layout>} />
          <Route path="/payment-history" element={<Layout><PaymentHistory /></Layout>} />
          <Route path="/referral" element={<Layout><Referral /></Layout>} />
          <Route path="/claim-business" element={<Layout><ClaimBusiness /></Layout>} />
          <Route path="/payments" element={<Navigate to="/payment-history" replace />} />
          <Route path="/pay-order/:orderId" element={<Layout><PayOrderPage /></Layout>} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/mobile-pay" element={<MobilePay />} />
          <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
          <Route path="/contact-us" element={<Layout><ContactUs /></Layout>} />
          <Route path="/caterer-verification" element={<Layout><CatererVerification /></Layout>} />
          <Route path="/trust-score" element={<Layout><TrustScore /></Layout>} />
          <Route path="/escrow-flow" element={<Layout><EscrowFlow /></Layout>} />
          <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
          <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
          <Route path="/refund-policy" element={<Layout><RefundPolicy /></Layout>} />
          <Route path="/compliance" element={<Layout><Compliance /></Layout>} />
          <Route path="/acceptable-use-policy" element={<Layout><AcceptableUsePolicy /></Layout>} />
          <Route path="/shipping-policy" element={<Layout><ShippingPolicy /></Layout>} />
          <Route path="/returns-policy" element={<Layout><ReturnsPolicy /></Layout>} />
          <Route path="/submit-idea" element={<Layout><SubmitIdea /></Layout>} />
          <Route path="/report-problem" element={<Layout><ReportProblem /></Layout>} />
          <Route path="/delete-account" element={<Layout><DeleteAccount /></Layout>} />
          <Route path="/404" element={<Layout><NotFound /></Layout>} />
          <Route path="*" element={<Layout><NotFound /></Layout>} />
        </RouterRoutes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default Routes;
